"use client";

import clsxm from "@/lib/clsxm";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {AnimatePresence, motion} from "framer-motion";
import {useState, type ComponentPropsWithoutRef, type FC, type RefObject} from "react";

const Popover = PopoverPrimitive.Root;
const PopoverAnchor = PopoverPrimitive.Anchor;
const PopoverPortal = PopoverPrimitive.Portal;
const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent: FC<
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    ref?: RefObject<HTMLDivElement>;
    wrapperClassName?: string;
    disableAnimation?: boolean;
  }
> = ({
  ref,
  className,
  wrapperClassName,
  align = "start",
  sideOffset = 4,
  disableAnimation = false,
  onPointerDownOutside,
  onEscapeKeyDown,
  onOpenAutoFocus,
  onCloseAutoFocus,
  children,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const innerClassName = clsxm("bg-popover text-popover-foreground border p-4 shadow-md", className);

  return (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      avoidCollisions
      collisionPadding={8}
      className={clsxm("z-50 w-72 rounded-md p-0 outline-none", wrapperClassName)}
      onPointerDownOutside={e => {
        setOpen(false);
        onPointerDownOutside?.(e);
      }}
      onEscapeKeyDown={e => {
        setOpen(false);
        onEscapeKeyDown?.(e);
      }}
      onOpenAutoFocus={e => {
        setOpen(true);
        onOpenAutoFocus?.(e);
      }}
      onCloseAutoFocus={e => {
        setOpen(false);
        onCloseAutoFocus?.(e);
      }}
      {...props}>
      {disableAnimation ? (
        <div className={innerClassName}>{children}</div>
      ) : (
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{opacity: 0, scale: 0.95, y: -10}}
              animate={{opacity: 1, scale: 1, y: 0}}
              exit={{opacity: 0, scale: 0.95, y: -10}}
              transition={{duration: 0.1}}
              style={{transformOrigin: "var(--radix-popover-content-transform-origin)"}}
              className={innerClassName}>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </PopoverPrimitive.Content>
  );
};
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export {Popover, PopoverAnchor, PopoverContent, PopoverPortal, PopoverTrigger};
